import { Icon, Popup, Table } from 'semantic-ui-react';
import ProvenanceValueCell from '../../../../../../../provenanceValueCell';
import { useSourceView } from '../../../../../../../sourceView';
import { DIFF_TYPE } from '../../../../../../../../constnats/diff';
import { BranchTypes } from '../../../../../../../../constnats/schemaConstants';
import type { FieldProvenanceType } from '../../../../../../../../schemas/report.schemas';
import { EMPTY_DIFF_VALUE_LABEL, REPORT_CELL_DIFF_KIND } from '../../constants';
import type { ReportCellDiffKind, ReportDiffCellData } from '../../types';

const DIFFERENCE_CLASS_BY_KIND: Record<ReportCellDiffKind, string> = {
    [REPORT_CELL_DIFF_KIND.VALUE]: 'branch-comparison-details__cell--value-difference',
    [REPORT_CELL_DIFF_KIND.CALCULATED_VALUE]: 'branch-comparison-details__cell--calculated-value-difference',
    [REPORT_CELL_DIFF_KIND.OTHER]: 'branch-comparison-details__cell--other-difference',
};

type ReportDiffCellProps = ReportDiffCellData & {
    label: string;
    reportName: string;
    columnLabels: { reference: string; target: string };
    targetReportId?: number;
    referenceReportId?: number;
};

function ProvenanceAction({
    provenance,
    onValueClick,
}: {
    provenance?: FieldProvenanceType[];
    onValueClick: (provenance: FieldProvenanceType[] | null) => void;
}) {
    return provenance?.length ? (
        <ProvenanceValueCell value="View source" provenance={provenance} clickable onClick={onValueClick} />
    ) : (
        <>No source</>
    );
}

function buildClassName(reportDiffType: ReportDiffCellData['reportDiffType'], differenceKind?: ReportCellDiffKind) {
    const classNames = new Set([
        'branch-comparison-details__cell',
        `branch-comparison-details__cell--${reportDiffType.toLowerCase()}`,
    ]);

    if (differenceKind) {
        classNames.add('branch-comparison-details__cell--changed');
        classNames.add(DIFFERENCE_CLASS_BY_KIND[differenceKind]);
    }

    return Array.from(classNames).join(' ');
}

function ReportDiffCell({
    value,
    reportDiffType,
    differenceKind,
    label,
    reportName,
    differences = {},
    columnLabels,
    referenceDescriptorId,
    targetDescriptorId,
    targetReportId,
    referenceReportId,
}: ReportDiffCellProps) {
    const { sourceViewEnabled, getProvenance, onValueClick } = useSourceView();
    const propertyDifferences = Object.entries(differences);
    const isDifferingCell = reportDiffType !== DIFF_TYPE.CHANGED || propertyDifferences.length > 0;
    const targetProvenance =
        sourceViewEnabled && isDifferingCell ? getProvenance(targetReportId, targetDescriptorId) : undefined;
    const referenceProvenance =
        sourceViewEnabled && isDifferingCell ? getProvenance(referenceReportId, referenceDescriptorId) : undefined;
    const hasProvenance = !!targetProvenance?.length || !!referenceProvenance?.length;
    const reviewProvenance = columnLabels.target === BranchTypes.REVIEW
        ? targetProvenance
        : columnLabels.reference === BranchTypes.REVIEW
            ? referenceProvenance
            : undefined;
    const sourceIcon = hasProvenance ? (
        reportDiffType === DIFF_TYPE.CHANGED && reviewProvenance?.length ? (
            <button
                type="button"
                className="branch-comparison-details__source-action"
                aria-label={`View Review provenance for ${label} in ${reportName}`}
                onClick={(event) => {
                    event.stopPropagation();
                    onValueClick(reviewProvenance);
                }}
            >
                <Icon name="info" color="blue" />
            </button>
        ) : (
            <Icon name="info" color="blue" title="Source available" />
        )
    ) : null;
    const directProvenance = reportDiffType === DIFF_TYPE.REMOVED ? referenceProvenance : targetProvenance;
    const displayValue = (
        <span className="branch-comparison-details__cell-content">
            {sourceIcon}
            <span className="branch-comparison-details__cell-value">{value}</span>
        </span>
    );
    const cellValue =
        reportDiffType === DIFF_TYPE.CHANGED ? (
            displayValue
        ) : (
            <ProvenanceValueCell
                value={displayValue}
                provenance={directProvenance}
                clickable={!!directProvenance?.length}
                onClick={onValueClick}
            />
        );
    const trigger = <td className={buildClassName(reportDiffType, differenceKind)}>{cellValue}</td>;

    if (propertyDifferences.length === 0) {
        return trigger;
    }

    return (
        <Popup
            on="click"
            flowing
            className="branch-comparison-details__popup"
            trigger={trigger}
            content={
                <div className="branch-comparison-details__popup-content">
                    <strong>{label}</strong>
                    <div>{reportName}</div>
                    <Table compact celled size="small">
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Property</Table.HeaderCell>
                                <Table.HeaderCell>{columnLabels.target}</Table.HeaderCell>
                                <Table.HeaderCell>{columnLabels.reference}</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {propertyDifferences.map(([property, difference]) => (
                                <Table.Row key={property}>
                                    <Table.Cell>{property}</Table.Cell>
                                    <Table.Cell>{difference.targetVal || EMPTY_DIFF_VALUE_LABEL}</Table.Cell>
                                    <Table.Cell>{difference.referenceVal || EMPTY_DIFF_VALUE_LABEL}</Table.Cell>
                                </Table.Row>
                            ))}
                            {sourceViewEnabled && (
                                <Table.Row>
                                    <Table.Cell>Provenance</Table.Cell>
                                    <Table.Cell>
                                        <ProvenanceAction provenance={targetProvenance} onValueClick={onValueClick} />
                                    </Table.Cell>
                                    <Table.Cell>
                                        <ProvenanceAction
                                            provenance={referenceProvenance}
                                            onValueClick={onValueClick}
                                        />
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </div>
            }
        />
    );
}

export default ReportDiffCell;
